import React, { useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { unixDiff, unixFormat } from '../../../helpers/times';

import './style.scss';

const DRAG_THRESHOLD = 5;

export default React.forwardRef(({
    stream,
    episodes,
    timeline,
    card,
    zoomValue,
    pageType,
    onEpisodeClick,
    setSlideOffset,
    onScheduleMove,
    children,
    handleBookmark,
    bookmarkEpisodeIds,
    isBookmarkVisible,
    currentUser,
    checkAuth,
    onRateEpisode,
}, ref) => {
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const isMouseDown = useRef(false);
    const isClickDisabled = useRef(false);

    function handleMouseDown(event) {
        isMouseDown.current = true;

        if (isMobile) return;

        startX.current = event.pageX - ref.current.offsetLeft;
        scrollLeft.current = ref.current.scrollLeft;
        ref.current.classList.add('draggabled');
    }

    function handleMouseUp() {
        isMouseDown.current = false;

        if (isMobile) return;

        setTimeout(() => {
            if (ref.current) {
                ref.current.classList.remove('draggabled');
            }

            isClickDisabled.current = false;
        });
    }

    function handleMouseMove(event) {
        if (isMobile) {
            if (isMouseDown.current) {
                onScheduleMove(true);
            }
            return;
        }

        if (!isMouseDown.current) return;

        const x = event.pageX - ref.current.offsetLeft;
        const scroll = x - startX.current;

        if (Math.abs(scroll) > DRAG_THRESHOLD) {
            onScheduleMove(true);
            isClickDisabled.current = true;
            setSlideOffset(scrollLeft.current - scroll);
        }
    }

    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const handleCardMouseEnter = (el, expanded) => {
        if (!el) return;
        const container = ref.current;
        const cardRightEdge = el.offsetLeft + el.offsetWidth + 20;

        if (container && expanded && cardRightEdge > container.scrollWidth) {
            setTimeout(() => {
                container.scrollLeft = container.scrollWidth;
            }, 100);
        }
    };

    return (
        <div
            ref={ref}
            className="broadcast-schedule container-scroll container-scroll--horizontal"
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onTouchMove={handleMouseMove}
        >
            {
                timeline?.intervals.map((unix, index, arr) => {
                    return (
                        index < arr.length - 1 &&
                        <div
                            key={unix}
                            className="broadcast-schedule__interval"
                            style={{
                                width: unixDiff(unix, arr[index + 1]) * zoomValue
                            }}
                        >
                            <span className="broadcast-schedule__interval-time">
                                {unixFormat(unix, 'HH:mm')}
                            </span>
                        </div>
                    )
                }) ?? null
            }
            {
                !!episodes?.length &&
                episodes.map((episode) => React.createElement(
                    card,
                    {
                        key: episode.id,
                        episode,
                        stream,
                        pageType,
                        className: 'broadcast-schedule__episode',
                        onClick: () => onEpisodeClick(!isClickDisabled.current ? episode : null),
                        style: {
                            left: unixDiff(timeline.timeStart, episode.timelineStart) * zoomValue + 1,
                            width: (unixDiff(episode.timelineStart, episode.timelineStop) || 1) * zoomValue - 2,
                        },
                        handleBookmark,
                        bookmarkEpisodeIds,
                        isBookmarkVisible,
                        currentUser,
                        checkAuth,
                        onRateEpisode,
                        onMouseEnter: handleCardMouseEnter,
                    }
                ))
            }
            {children}
        </div>
    )
});
